import React from "react";
import ReactMarkdown from "react-markdown";
import { FileText, Gavel, ListChecks, StickyNote, ScrollText, Users, Scale, BookOpen, Sparkles, Pin, Plus } from "lucide-react";

const ICONS = { Doc: FileText, Hearing: Gavel, Task: ListChecks, Note: StickyNote, Order: ScrollText, Party: Users, Matter: Scale, Authority: BookOpen };
const plain = (children) => React.Children.toArray(children).map((c) => (typeof c === "string" ? c : c?.props?.children ? plain(c.props.children) : "")).join("").replace(/\[\[\w+:\s*([^\]]+)\]\]/g, "$1").trim();

// Turns [[Type: Name]] citations emitted by Claude into inline source chips.
function withCitations(children) {
  return React.Children.map(children, (child) => {
    if (typeof child !== "string") return child;
    const parts = child.split(/(\[\[[^\]]+\]\])/g);
    return parts.map((p, i) => {
      const m = p.match(/^\[\[(\w+):\s*([^\]]+)\]\]$/);
      if (!m) return p;
      const Icon = ICONS[m[1]] || Sparkles;
      return (
        <span key={i} className="mx-0.5 inline-flex items-center gap-1 rounded bg-copper-light px-1.5 py-0.5 align-middle text-[11px] font-medium text-copper" data-testid="ai-citation">
          <Icon className="h-3 w-3" />{m[2]}
        </span>
      );
    });
  });
}

export function AIMarkdown({ text, className = "", onAction, actionSections = ["suggested next action"] }) {
  const section = { current: "" };
  const actionable = () => onAction && actionSections.some((s) => section.current.toLowerCase().includes(s));
  const heading = (children) => { section.current = plain(children); return <h3 className="mt-4 font-display text-lg font-semibold text-ink first:mt-0">{withCitations(children)}</h3>; };
  return (
    <div className={`ai-md text-sm leading-relaxed text-ink ${className}`}>
      <ReactMarkdown components={{
        h3: ({ children }) => heading(children),
        h2: ({ children }) => heading(children),
        p: ({ children }) => <p className="my-2 text-ink-2">{withCitations(children)}</p>,
        li: ({ children }) => (
          <li className="group my-1 ml-4 list-disc text-ink-2">{withCitations(children)}
            {actionable() && (
              <span className="ml-2 inline-flex gap-1 align-middle opacity-70 group-hover:opacity-100">
                <button onClick={() => onAction("task", plain(children))} className="inline-flex items-center gap-0.5 rounded border border-line bg-card px-1.5 py-0.5 text-[10px] text-ink-2 hover:border-copper hover:text-copper" data-testid="brief-to-task"><Plus className="h-3 w-3" /> Task</button>
                <button onClick={() => onAction("pin", plain(children))} className="inline-flex items-center gap-0.5 rounded border border-line bg-card px-1.5 py-0.5 text-[10px] text-ink-2 hover:border-copper hover:text-copper" data-testid="brief-to-pin"><Pin className="h-3 w-3" /> Pin</button>
              </span>
            )}
          </li>
        ),
        strong: ({ children }) => <strong className="font-semibold text-ink">{withCitations(children)}</strong>,
        em: ({ children }) => <em>{withCitations(children)}</em>,
      }}>{text}</ReactMarkdown>
    </div>
  );
}
